import { createContext, ReactNode, useState } from 'react';
import { ReactFlowInstance, Node } from '@xyflow/react';
import { useDispatch } from 'react-redux';
import { getUniqueNodeId } from '../../utils/genericHelper';
import { cloneDeep } from 'lodash';
import { SET_DIRTY } from '../actions';

const initialValue: {
    reactFlowInstance: ReactFlowInstance | null;
    setReactFlowInstance: (instance: ReactFlowInstance | null) => void;
    duplicateNode: (id: string) => void;
    deleteNode: (nodeid: string) => void;
    deleteEdge: (edgeid: string) => void;
} = {
    reactFlowInstance: null,
    setReactFlowInstance: () => { },
    duplicateNode: () => { },
    deleteNode: () => { },
    deleteEdge: () => { },
};

interface ReactFlowContextProps {
    children: ReactNode;
}

export const FlowContext = createContext(initialValue);


export const ReactFlowContext = ({ children }: ReactFlowContextProps) => {
    const dispatch = useDispatch();
    const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance | null>(null);

    const deleteNode = (nodeid: string) => {
        if (!reactFlowInstance) return;
        deleteConnectedInput(nodeid, 'node');
        reactFlowInstance.setNodes(reactFlowInstance.getNodes().filter((n) => n.id !== nodeid));
        reactFlowInstance.setEdges(reactFlowInstance.getEdges().filter((ns) => ns.source !== nodeid && ns.target !== nodeid));
        dispatch({ type: SET_DIRTY });
    };

    const deleteEdge = (edgeid: string) => {
        if (!reactFlowInstance) return;
        deleteConnectedInput(edgeid, 'edge');
        reactFlowInstance.setEdges(reactFlowInstance.getEdges().filter((edge) => edge.id !== edgeid));
        dispatch({ type: SET_DIRTY });
    };

    const deleteConnectedInput = (id: string, type: 'node' | 'edge') => {
        if (!reactFlowInstance) return;
        const connectedEdges = type === 'node'
            ? reactFlowInstance.getEdges().filter((edge) => edge.source === id)
            : reactFlowInstance.getEdges().filter((edge) => edge.id === id);

        for (const edge of connectedEdges) {
            const targetNodeId = edge.target;
            const sourceNodeId = edge.source;
            const targetInput: any = edge.targetHandle?.split('-')[2];

            reactFlowInstance.setNodes((nds: Node[]) =>
                nds.map((node: any) => {
                    if (node.id === targetNodeId) {
                        let value;
                        const inputAnchors = node.data.inputAnchors;
                        const inputParams = node.data.inputParams;
                        const inputAnchor = inputAnchors.find((ancr) => ancr.name === targetInput);
                        const inputParam = inputParams.find((param) => param.name === targetInput);

                        if (inputAnchor && inputAnchor.list) {
                            const values = node.data.inputs[targetInput] || [];
                            value = values.filter((item) => !item?.includes(sourceNodeId));
                        } else if (inputParam && inputParam.acceptVariable) {
                            value = node.data.inputs[targetInput].replace(`{{${sourceNodeId}.data.instance}}`, '') || '';
                        } else {
                            value = '';
                        }
                        node.data = {
                            ...node.data,
                            inputs: {
                                ...node.data.inputs,
                                [targetInput]: value,
                            },
                        };
                    }
                    return node;
                })
            );
        }
    };

    const duplicateNode = (id: string) => {
        if (!reactFlowInstance) return;
        const nodes = reactFlowInstance.getNodes();
        const originalNode = nodes.find((n) => n.id === id);
        if (originalNode) {
            const newNodeId = getUniqueNodeId(originalNode.data, nodes);
            const clonedNode: any = cloneDeep(originalNode);

            const duplicatedNode = {
                ...clonedNode,
                id: newNodeId,
                position: {
                    x: clonedNode.position.x + 400,
                    y: clonedNode.position.y,
                },
                positionAbsolute: {
                    x: clonedNode.positionAbsolute.x + 400,
                    y: clonedNode.positionAbsolute.y,
                },
                data: {
                    ...clonedNode.data,
                    id: newNodeId,
                },
                selected: false,
            };

            const dataKeys = ['inputParams', 'inputAnchors', 'outputAnchors'];

            for (const key of dataKeys) {
                for (const item of duplicatedNode.data[key]) {
                    if (item.id) {
                        item.id = item.id.replace(id, newNodeId);
                    }
                }
            }

            reactFlowInstance.setNodes([...nodes, duplicatedNode]);
            dispatch({ type: SET_DIRTY });
        }
    };

    return (
        <FlowContext.Provider
            value= {{
        reactFlowInstance,
            setReactFlowInstance,
            deleteNode,
            deleteEdge,
            duplicateNode,
            }}
        >
            { children }
        </FlowContext.Provider>
    );
};

